// The normalized entity store — the framework-agnostic core of the Data layer.
//
// Every model is kept exactly once by (entityType, id), indexed by its owner-FK
// fields (derived from the schema graph via the connection layer). A single
// centralized merge (`applyLive`) upserts or removes entities; one subscription
// per entity type routes live broadcasts into it, activated by a ref-counted
// retain/release lifecycle; identical concurrent fetches dedupe by key. React
// binds to it through `useSyncExternalStore` (see `useStore.ts`), so re-render
// granularity comes from referentially-stable snapshots: an unrelated entity's
// change never produces a new query array.
//
// #1707 (tracker #1677) extracted that entire generic engine into app-base's
// `data/store` (parameterized by a `DataSchema`, with no knowledge of Logbook's
// generated types or the two id-0 "uncommitted" log channels below). `DataStore`
// here `extends` it: every generic method (`getEntity`, `getQuery`, `retain`,
// `release`, `fetchQuery`, `applyBundle`, the diagnostics reads, …) is inherited
// unchanged, so this file owns only what the generic engine deliberately does
// not — the current/provisional log extension, Logbook's `ApiClient`-typed
// mutation surface, and the merged Logbook diagnostics status (see each
// section's own comment). Two methods are overridden, both with the *same*
// public signature as their base version (`subscribe`, `setDiagnosticTrace`) so
// the override stays a pure addition, not a narrowing; `applyLive`/`mutate` are
// overridden to keep their prior, stricter Logbook-only parameter types
// (`LiveInfo`, `ApiClient`) — see each for why that's safe.
import {
  DataStore as GenericDataStore,
  type DataStoreDeps as GenericDataStoreDeps,
  type DataStoreDiagnosticEvent,
  type DataStoreDiagnosticStatus as GenericDataStoreDiagnosticStatus,
  type DataStoreEntitySnapshot,
  type DataStoreExtrasSnapshot,
  type MutationChanges as GenericMutationChanges,
  type MutationSpec as GenericMutationSpec,
  type Seed as GenericSeed,
} from "app-base/data/store"
import {
  connections as defaultConnections,
  toGenericConnections,
  type EntityIds,
  type EntityOf,
  type FetchResult,
  type Identified,
  type LogbookSchema,
  logbookSchemaDescriptor,
} from "./Connections"
import type { ConnectionRegistry } from "./Connections"
import type { ApiClient } from "../Types/Generated/LogbookApi/ApiRuntime"
import type { SchemaEntity } from "../Types/Generated/LogbookModel/Schema"
import { Log, type ILog } from "../Types/Generated/LogbookModel/Log"
import type { IShipUser } from "../Types/Generated/LogbookModel/ShipUser"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { brandCurrent, brandProvisional, type ICurrentLog, type IProvisionalLog } from "../Types/LogKind"
import { createLogger } from "../App/Logger"

const logger = createLogger("Store");
const diagnosticsLogger = createLogger("DataLayer");

// The subset of the app socket the store needs. `Client` satisfies it structurally
// (its `subscribe` overload set includes this generic form), so the real app injects
// its live socket and tests inject a fake/spy. Kept as Logbook's own generic-`subscribe`
// shape (rather than re-exporting the generic engine's own non-generic `DataSocket`, whose
// callback always takes `unknown`) because the two uncommitted-log slices below call
// `this.uncommittedSocket.subscribe<ILog>(...)` directly — they are not schema entities,
// so they have no `Connection`/index to ride through the inherited generic
// retain/release. A value of this (more permissive, generic) shape already satisfies the
// generic engine's own `DataSocket` structurally, so no adapter is needed passing it to
// `super(...)`.
export interface DataSocket {
  subscribe<T>(type: string, callback: (data: T, type: string, info: string) => void): number;
  unsubscribe(type: string, id: number): void;
}

// Wraps the app socket so a live `ShipUser` broadcast that collides with an
// already-stored row under the same `id` but a *different* `shipId` never
// reaches the generic engine's live-ingress path at all.
//
// This is the real interception seam for that guard: app-base's `retain()`
// (`node_modules/app-base/.../data/store.ts`) calls `socket.subscribe(type,
// callback)` once per retained type, and its callback invokes the private
// `applyLiveRaw` directly -- never the overridable `applyLive` above, which
// only ever runs for this store's own test-simulated `store.applyLive(...)`
// calls. Filtering here, before the callback the generic engine itself
// registers ever runs, is the only place a subclass can act on a real
// broadcast without touching `app-base`.
//
// `ShipUser.id` is only unique *within* its own ship (`Connections/Ship.ts`'s
// all-ships fetch comment), so this collision is reachable in production
// whenever a client is (still) subscribed to more than one ship's channel at
// once -- e.g. `ShipView.tsx`'s per-ship channel subscribe/unsubscribe effect
// leaking a stale subscription across a ship switch (the #1716-class
// regression that motivated this guard; see its own fix and comment). A
// same-id broadcast from that stale, backgrounded ship would otherwise
// silently overwrite the row serving the ship actually open, corrupting its
// `shipId` field -- which `AppView.tsx`'s eviction effect (keyed off exactly
// that field) would then read as the acting user's own access having been
// revoked.
type DataLiveListener<K extends SchemaEntity = SchemaEntity> = (entity: EntityOf<K>, info: LiveInfo) => void;

function dataLayerSocket(
  socket: DataSocket,
  getShipUser: (id: number) => IShipUser | undefined,
  publishLive: (type: SchemaEntity, entity: Identified, info: LiveInfo) => void,
): DataSocket {
  return {
    subscribe<T>(type: string, callback: (data: T, type: string, info: string) => void): number {
      return socket.subscribe<T>(type, (data, wireType, info) => {
        if (type === "ShipUser"
          && isCrossShipShipUserCollision(data as Partial<IShipUser>, getShipUser)) {
          return;
        }
        callback(data, wireType, info);
        publishLive((type.startsWith("StoredCalculation/") ? "StoredCalculation" : type) as SchemaEntity, data as Identified, asLiveInfo(info));
      });
    },
    unsubscribe(type: string, id: number): void {
      socket.unsubscribe(type, id);
    },
  };
}

function isCrossShipShipUserCollision(
  incoming: Partial<IShipUser>,
  getShipUser: (id: number) => IShipUser | undefined,
): boolean {
  if (typeof incoming.id !== "number" || typeof incoming.shipId !== "number") {
    return false;
  }
  const existing = getShipUser(incoming.id);
  return existing !== undefined && existing.shipId !== incoming.shipId;
}

// Initial rows to prime the store with at construction, keyed by entity type.
// A store seeded with no `socket`/`apiClient` is a static, offline fixture store
// (no fetch, no live); a seeded LIVE store keeps fetching and subscribing, using
// the seed only as an optimistic initial fill.
export type Seed = GenericSeed<LogbookSchema>;

// A batch of store changes — rows to upsert and ids to remove, keyed by entity
// type. The unit an optimistic write (and its later reconcile) is expressed in.
export type MutationChanges = GenericMutationChanges<LogbookSchema>;

export type { DataStoreDiagnosticEvent, DataStoreEntitySnapshot, DataStoreExtrasSnapshot }

export type DataStoreDiagnosticStatus = GenericDataStoreDiagnosticStatus;

export interface DataStoreDeps {
  // Both transports are optional: without a `socket` the store never subscribes,
  // and without an `apiClient` it never fetches — it just serves its seeded/live
  // contents. This is what makes a seed-only fixture store possible.
  socket?: DataSocket;
  apiClient?: ApiClient;
  // Defaults to the real registry; tests may inject a narrowed one.
  connections?: ConnectionRegistry;
  // Rows to normalize into the store at construction.
  seed?: Seed;
  // Initial fills for the two id-0 "uncommitted" log slices (see `getCurrentLog` /
  // `getProvisionalLogs`). Branded on ingest; kept out of the committed `Log` store.
  currentLog?: ILog;
  provisionalLogs?: readonly ILog[];
  // Baseline events go to the native support log by default; tests can inject
  // sinks to assert the compact, non-sensitive envelopes.
  supportLog?: (event: DataStoreDiagnosticEvent) => void;
  traceLog?: (event: DataStoreDiagnosticEvent) => void;
}

// The operation a live broadcast carries, mirroring `BroadcastOperation` in
// Sources/LogbookApi/Broadcast.swift. The wire delivers it as a bare `string`, so the
// socket boundary narrows it once (`asLiveInfo`) and every `apply*` below takes the union
// — a mistyped `"delele"` would otherwise read as "not a delete" and upsert a row the
// server just removed.
export type LiveInfo =
  | "complete"
  | "create"
  | "delete"
  // Not a `BroadcastOperation`: a client-originated marker for rows seeded from a REST
  // load rather than a broadcast (see FocusView's provisional seed, #1218). Carried here
  // so a seed and a live upsert take exactly the same path; only `"delete"` diverges.
  | "load"
  | "schedule"
  | "set"
  | "start"
  | "stop"
  | "sync"
  | "update";

// The single narrowing point for the wire's untyped `info`. An unrecognized operation is
// passed through as-is: only `"delete"` changes behavior, and every other value already
// means "upsert" — so a new server-side operation stays forward-compatible.
function asLiveInfo(info: string): LiveInfo {
  return info as LiveInfo;
}

// A typed optimistic write: what to apply immediately, the typed REST call that commits
// it, and how to reconcile the authoritative response back into the store. Generic over
// the response type `R` so a caller keeps its typed `ILogResponse`/`IDayResponse` etc.
// See `DataStore.mutate`. Takes Logbook's concrete generated `ApiClient` (not the generic
// engine's `DataApiClient`) for the same reason `Connection.fetch` does — see
// `Connections/Connection.ts`'s header.
export interface MutationSpec<R> {
  // Changes applied immediately, before the request settles, so an edit shows at
  // once. Rolled back (guarded) if the request rejects.
  optimistic?: MutationChanges;
  // Provisional temp rows to drop once the authoritative server row lands — a
  // create's temp-id → real-id remap. Removed on success, after `reconcile`; the
  // rollback already drops them on failure (their pre-image is "absent").
  provisional?: EntityIds;
  // The typed REST call producing the authoritative response.
  request: (client: ApiClient) => Promise<R>;
  // The authoritative entities carried by the response, upserted on success. Reuses
  // the same side-normalizing `FetchResult` bundle a fetch produces.
  reconcile?: (response: R) => FetchResult;
}

const EMPTY_PROVISIONAL: readonly IProvisionalLog[] = Object.freeze([]);

function compareDiagnosticType(a: { type: string }, b: { type: string }): number {
  return a.type.localeCompare(b.type);
}

// Recursively freezes a plain value tree — the Logbook-side counterpart of the
// generic engine's own (unexported) snapshot freezing, needed here only for
// the diagnostics this file merges in on top of the inherited generic status
// (see `getDiagnosticStatus`) and the uncommitted-log event envelopes below.
function deepFreeze<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => deepFreeze(entry))) as T;
  }
  if (value && typeof value === "object") {
    const copy: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      copy[key] = deepFreeze(entry);
    }
    return Object.freeze(copy) as T;
  }
  return value;
}

export class DataStore extends GenericDataStore<LogbookSchema> {
  // The generic engine's own listener set (`subscribe`/`notify`) is private and
  // therefore inaccessible from this subclass; the two uncommitted-log slices
  // below are store state the generic engine has no notion of, so they keep
  // their own listener set and are folded into the same public `subscribe`
  // (override below) — preserving the pre-extraction contract that one
  // subscription observes entity AND uncommitted-log changes alike.
  private readonly uncommittedListeners = new Set<() => void>();
  private readonly dataLiveListeners = new Map<SchemaEntity, Set<DataLiveListener>>();
  private readonly diagnosticsSupportLog: (event: DataStoreDiagnosticEvent) => void;
  private readonly diagnosticsTraceLog: (event: DataStoreDiagnosticEvent) => void;
  private uncommittedTraceEnabled = false;
  // A private copy of the socket: the generic engine's own `socket` field is
  // private, and the two uncommitted-log channels below subscribe directly
  // (they are not schema entities, so they have no `Connection`/index to ride).
  private readonly uncommittedSocket?: DataSocket;
  private readonly logbookApiClient?: ApiClient;

  // ---- uncommitted (id-0) log slices ------------------------------------
  // The two id-0 "uncommitted" log channels are kept OUTSIDE the normalized entity
  // store: they share id 0, so they can't be keyed by id and must never collide with
  // or leak into the committed `Log` timeline. Each is a first-class Data-layer slice
  // with its own ref-counted live subscription, but reuses the store's single listener
  // set (`notify`) so React binds to them through the same `subscribe` contract.
  private currentLog: ICurrentLog | undefined;
  private readonly provisionalLogsByKey = new Map<string, IProvisionalLog>();
  // Memoized `getProvisionalLogs` snapshot; rebuilt lazily only after a change so an
  // unchanged read keeps the same reference (useSyncExternalStore bails out).
  private provisionalSnapshot: readonly IProvisionalLog[] = EMPTY_PROVISIONAL;
  private provisionalDirty = false;
  private currentLogRetain = 0;
  private currentLogSubId: number | undefined;
  private provisionalRetain = 0;
  private provisionalSubId: number | undefined;

  constructor(deps: DataStoreDeps = {}) {
    const supportLog = deps.supportLog ?? ((event) => diagnosticsLogger.native(event));
    // Trace volume is already explicitly gated by `logbook.dataLayer.trace(true)`.
    // Use the normally visible console stream so enabling it does not also require
    // turning on the browser inspector's hidden "Verbose"/debug level.
    const traceLog = deps.traceLog ?? ((event) => diagnosticsLogger.info(event));
    // Late-bound: the wrapped socket below needs to read this store's own state
    // (`getEntity`) to detect a cross-ship `ShipUser` collision, but `this` isn't
    // available until after `super()` runs. `guardShipUserCollisions` only invokes
    // this once a live `ShipUser` broadcast actually arrives -- never during
    // construction itself -- so filling it in immediately after `super()` is safe.
    let getShipUser: (id: number) => IShipUser | undefined = () => undefined;
    let publishLive: (type: SchemaEntity, entity: Identified, info: LiveInfo) => void = () => {};
    const genericDeps: GenericDataStoreDeps<LogbookSchema> = {
      connections: toGenericConnections(deps.connections ?? defaultConnections),
      edges: logbookSchemaDescriptor.edges,
      socket: deps.socket && dataLayerSocket(
        deps.socket,
        (id) => getShipUser(id),
        (type, entity, info) => publishLive(type, entity, info),
      ),
      apiClient: deps.apiClient,
      seed: deps.seed,
      supportLog,
      traceLog,
      debugLog: (message, ...values) => logger.debug(message, ...values),
    };
    super(genericDeps);
    getShipUser = (id) => this.getEntity("ShipUser", id) as IShipUser | undefined;
    publishLive = (type, entity, info) => this.publishLive(type, entity, info);
    this.uncommittedSocket = deps.socket;
    this.logbookApiClient = deps.apiClient;
    this.diagnosticsSupportLog = supportLog;
    this.diagnosticsTraceLog = traceLog;
    if (deps.currentLog) {
      this.applyCurrentLog(deps.currentLog, "update");
    }
    if (deps.provisionalLogs) {
      for (const row of deps.provisionalLogs) {
        this.applyProvisionalLog(row, "update");
      }
    }
  }

  // ---- shared listener set (entity + uncommitted-log changes) -----------

  override subscribe(listener: () => void): () => void {
    const unsubscribe = super.subscribe(listener);
    this.uncommittedListeners.add(listener);
    return () => {
      unsubscribe();
      this.uncommittedListeners.delete(listener);
    };
  }

  private notifyUncommitted(): void {
    for (const listener of this.uncommittedListeners) {
      listener();
    }
  }

  api(): ApiClient {
    if (!this.logbookApiClient) {
      throw new Error("DataStore.api: no apiClient (offline store)");
    }
    return this.logbookApiClient;
  }

  subscribeLiveEvent<K extends SchemaEntity>(
    type: K,
    listener: DataLiveListener<K>,
  ): () => void {
    let listeners = this.dataLiveListeners.get(type);
    if (!listeners) {
      listeners = new Set();
      this.dataLiveListeners.set(type, listeners);
    }
    listeners.add(listener as DataLiveListener);
    return () => {
      listeners?.delete(listener as DataLiveListener);
      if (listeners?.size === 0) {
        this.dataLiveListeners.delete(type);
      }
    };
  }

  private publishLive(type: SchemaEntity, entity: Identified, info: LiveInfo): void {
    for (const listener of this.dataLiveListeners.get(type) ?? []) {
      listener(entity as never, info);
    }
  }

  // ---- typed live ingress / mutation --------------------------------------

  // Narrows `info` back to the closed `LiveInfo` union the generic engine's
  // own `applyLive<K>(type, entity, info: string)` deliberately widens (every
  // other consuming app has its own live vocabulary). Every existing caller
  // already passes a `LiveInfo`-shaped literal, so this is a pure strictness
  // restoration, not a behavior change.
  //
  // Also closes the same `ShipUser` identity-boundary gap the socket wrapper
  // below (`guardShipUserCollisions`) closes for real live broadcasts -- but
  // for a *different* set of callers: this store's own `retain()` subscribes
  // straight to `socket.subscribe(...)` and hands broadcasts to the generic
  // engine's private `applyLiveRaw` directly, never through this overridable
  // `applyLive`, so a guard here alone would never see a real broadcast. It's
  // kept because `store.applyLive(...)` is this codebase's own established
  // pattern for *simulating* a live update from a test (see `Store.test.ts`
  // and friends), so guarding it too keeps that pattern trustworthy.
  override applyLive<K extends SchemaEntity>(type: K, entity: EntityOf<K> | Identified, info: LiveInfo): void {
    if (type === "ShipUser" && this.isCrossShipShipUserCollision(entity as Partial<IShipUser>)) {
      return;
    }
    super.applyLive(type, entity, info);
  }

  private isCrossShipShipUserCollision(incoming: Partial<IShipUser>): boolean {
    if (typeof incoming.id !== "number" || typeof incoming.shipId !== "number") {
      return false;
    }
    const existing = this.getEntity("ShipUser", incoming.id) as IShipUser | undefined;
    return existing !== undefined && existing.shipId !== incoming.shipId;
  }

  // Perform one write through the layer: optimistically apply
  // `spec.optimistic`, run the typed REST `spec.request`, then on success
  // reconcile the authoritative response into the store, or on failure roll
  // the optimistic write back and rethrow — all inherited from the generic
  // engine unchanged. This override exists only to keep `MutationSpec.request`
  // typed against Logbook's concrete `ApiClient` (see this file's `MutationSpec`
  // doc comment); a real `ApiClient` value always satisfies the generic
  // engine's own `DataApiClient` structurally, so widening the spec here is
  // exactly the same safe relationship `Connections/Connection.ts#entity()`
  // asserts for a connection's `fetch`.
  override async mutate<R>(spec: MutationSpec<R>): Promise<R> {
    return super.mutate(spec as GenericMutationSpec<LogbookSchema, R>);
  }

  // ---- diagnostics ---------------------------------------------------------

  reportDiagnosticsAttached(): void {
    this.diagnosticsSupportLog(deepFreeze({ event: "store.attached" }));
  }

  // Tracks tracing locally too: the generic engine's own trace-enabled flag is
  // private, so the uncommitted-log slice's own trace emission (below) cannot
  // read it directly. `noImplicitOverride`-safe: same signature as the base.
  override setDiagnosticTrace(enabled: boolean): boolean {
    this.uncommittedTraceEnabled = enabled;
    return super.setDiagnosticTrace(enabled);
  }

  // The generic engine's own status (entity counts, retain/subscription state
  // for schema-entity connections, in-flight fetches) plus this file's own
  // extensions: the Logbook `feature` flag envelope (diagnostics global naming
  // is Logbook policy — see `Diagnostics.ts`) and the two uncommitted-log
  // slices' retain/subscription state, which the generic engine has no notion
  // of at all. Covariant override: same zero-arg signature as the base, a
  // strict superset return type.
  override getDiagnosticStatus(): DataStoreDiagnosticStatus {
    const base = super.getDiagnosticStatus();
    const retained = [...base.retained];
    const subscriptions = [...base.subscriptions];
    if (this.currentLogRetain > 0) {
      retained.push({ type: Subscription.currentLog.type, count: this.currentLogRetain });
    }
    if (this.provisionalRetain > 0) {
      retained.push({ type: Subscription.provisionalLog.type, count: this.provisionalRetain });
    }
    if (this.currentLogRetain > 0 || this.currentLogSubId !== undefined) {
      subscriptions.push({
        type: Subscription.currentLog.type,
        active: this.currentLogSubId !== undefined,
        channels: [Subscription.currentLog.type],
      });
    }
    if (this.provisionalRetain > 0 || this.provisionalSubId !== undefined) {
      subscriptions.push({
        type: Subscription.provisionalLog.type,
        active: this.provisionalSubId !== undefined,
        channels: [Subscription.provisionalLog.type],
      });
    }
    retained.sort(compareDiagnosticType);
    subscriptions.sort(compareDiagnosticType);
    return deepFreeze({
      traceEnabled: base.traceEnabled,
      entityCounts: base.entityCounts,
      retained,
      subscriptions,
      inFlightFetches: base.inFlightFetches,
    });
  }

  private emitUncommittedSupport(event: DataStoreDiagnosticEvent): void {
    this.diagnosticsSupportLog(deepFreeze(event));
  }

  private emitUncommittedTrace(event: DataStoreDiagnosticEvent): void {
    if (this.uncommittedTraceEnabled) {
      this.diagnosticsTraceLog(deepFreeze(event));
    }
  }

  private emitUncommittedSubscription(operation: "start" | "stop", type: string, channels: readonly string[]): void {
    const event = { event: `subscription.${operation}`, type, channels };
    this.emitUncommittedSupport(event);
    this.emitUncommittedTrace(event);
  }

  private emitUncommittedLiveResult(
    type: string,
    id: number,
    operation: LiveInfo,
    deleted: boolean,
    kind: "added" | "updated" | "removed" | undefined,
  ): void {
    const event = deleted ? "live.delete" : "live.upsert";
    const results = [{ type, count: 1 }];
    const added = kind === "added" ? results : [];
    const updated = kind === "updated" ? results : [];
    const removed = kind === "removed" ? results : [];
    this.emitUncommittedSupport({ event, operation, count: 1, results, added, updated, removed });
    this.emitUncommittedTrace({ event, type, id, operation, count: 1, results, added, updated, removed });
  }

  // ---- uncommitted (id-0) log slices ------------------------------------

  // The singleton "now" entry (fed by `Log/Current`), or `undefined`. A stable object
  // reference until it is replaced/cleared.
  getCurrentLog(): ICurrentLog | undefined {
    return this.currentLog;
  }

  // The in-progress provisional auto-logs (fed by `Log/Provisional`), sorted by
  // `timeUtc`. Referentially stable until the set actually changes.
  getProvisionalLogs(): readonly IProvisionalLog[] {
    if (this.provisionalDirty) {
      this.provisionalSnapshot = this.provisionalLogsByKey.size === 0
        ? EMPTY_PROVISIONAL
        : Array.from(this.provisionalLogsByKey.values()).sort((a, b) => a.timeUtc - b.timeUtc);
      this.provisionalDirty = false;
    }
    return this.provisionalSnapshot;
  }

  // Ingest a `Log/Current` broadcast (or seed): replace the singleton, or clear it on a
  // delete. Branded `current` so a shared renderer can tell it apart from a committed log.
  applyCurrentLog(log: ILog, info: LiveInfo): void {
    this.currentLog = info === "delete" ? undefined : brandCurrent(log);
    this.notifyUncommitted();
  }

  // Ingest a `Log/Provisional` broadcast (or seed): upsert by `metadata.provisionalKey`,
  // or remove on a delete. A keyless log is ignored (mirrors `AppData.updateProvisional`).
  applyProvisionalLog(log: ILog, info: LiveInfo): void {
    const key = Log.parse(log).dataParsed.provisionalKey;
    if (!key) {
      return;
    }
    if (info === "delete") {
      if (!this.provisionalLogsByKey.delete(key)) {
        return;
      }
    } else {
      this.provisionalLogsByKey.set(key, brandProvisional(log));
    }
    this.provisionalDirty = true;
    this.notifyUncommitted();
  }

  // Replace the complete provisional set from an authoritative snapshot. Unlike the
  // per-broadcast `applyProvisionalLog`, absence is meaningful here: a key missing from
  // the snapshot was retracted while this client could not observe the live channel.
  replaceProvisionalLogs(logs: readonly ILog[]): void {
    if (logs.length === 0 && this.provisionalLogsByKey.size === 0) {
      return;
    }
    this.provisionalLogsByKey.clear();
    for (const log of logs) {
      const key = Log.parse(log).dataParsed.provisionalKey;
      if (key) {
        this.provisionalLogsByKey.set(key, brandProvisional(log));
      }
    }
    this.provisionalDirty = true;
    this.notifyUncommitted();
  }

  // Ref-count the `Log/Current` slice: the first retain subscribes its single live
  // handler, the matching last release tears it down.
  retainCurrentLog(): void {
    this.currentLogRetain += 1;
    this.emitUncommittedTrace({
      event: "retain.acquire",
      type: Subscription.currentLog.type,
      count: this.currentLogRetain,
    });
    if (this.currentLogRetain > 1 || !this.uncommittedSocket) {
      return;
    }
    this.currentLogSubId = this.uncommittedSocket.subscribe<ILog>(
      Subscription.currentLog.type,
      (data, _wireType, info) => this.applyCurrentLogLive(data, asLiveInfo(info)),
    );
    this.emitUncommittedSubscription("start", Subscription.currentLog.type, [Subscription.currentLog.type]);
  }

  releaseCurrentLog(): void {
    if (this.currentLogRetain <= 0) {
      return;
    }
    this.currentLogRetain -= 1;
    this.emitUncommittedTrace({
      event: "retain.release",
      type: Subscription.currentLog.type,
      count: this.currentLogRetain,
    });
    if (this.currentLogRetain > 0) {
      return;
    }
    if (this.uncommittedSocket && this.currentLogSubId !== undefined) {
      this.uncommittedSocket.unsubscribe(Subscription.currentLog.type, this.currentLogSubId);
      this.emitUncommittedSubscription("stop", Subscription.currentLog.type, [Subscription.currentLog.type]);
    }
    this.currentLogSubId = undefined;
  }

  // Ref-count the `Log/Provisional` slice (see `retainCurrentLog`).
  retainProvisionalLogs(): void {
    this.provisionalRetain += 1;
    this.emitUncommittedTrace({
      event: "retain.acquire",
      type: Subscription.provisionalLog.type,
      count: this.provisionalRetain,
    });
    if (this.provisionalRetain > 1 || !this.uncommittedSocket) {
      return;
    }
    this.provisionalSubId = this.uncommittedSocket.subscribe<ILog>(
      Subscription.provisionalLog.type,
      (data, _wireType, info) => this.applyProvisionalLogLive(data, asLiveInfo(info)),
    );
    this.emitUncommittedSubscription("start", Subscription.provisionalLog.type, [Subscription.provisionalLog.type]);
  }

  releaseProvisionalLogs(): void {
    if (this.provisionalRetain <= 0) {
      return;
    }
    this.provisionalRetain -= 1;
    this.emitUncommittedTrace({
      event: "retain.release",
      type: Subscription.provisionalLog.type,
      count: this.provisionalRetain,
    });
    if (this.provisionalRetain > 0) {
      return;
    }
    if (this.uncommittedSocket && this.provisionalSubId !== undefined) {
      this.uncommittedSocket.unsubscribe(Subscription.provisionalLog.type, this.provisionalSubId);
      this.emitUncommittedSubscription("stop", Subscription.provisionalLog.type, [Subscription.provisionalLog.type]);
    }
    this.provisionalSubId = undefined;
  }

  private applyCurrentLogLive(log: ILog, info: LiveInfo): void {
    const existed = this.currentLog !== undefined;
    this.applyCurrentLog(log, info);
    const kind = info === "delete" ? (existed ? "removed" : undefined) : (existed ? "updated" : "added");
    this.emitUncommittedLiveResult(Subscription.currentLog.type, log.id, info, info === "delete", kind);
  }

  private applyProvisionalLogLive(log: ILog, info: LiveInfo): void {
    const key = Log.parse(log).dataParsed.provisionalKey;
    if (!key) {
      return;
    }
    const existed = this.provisionalLogsByKey.has(key);
    this.applyProvisionalLog(log, info);
    const kind = info === "delete" ? (existed ? "removed" : undefined) : (existed ? "updated" : "added");
    this.emitUncommittedLiveResult(Subscription.provisionalLog.type, log.id, info, info === "delete", kind);
  }
}
